/**
 * Iter 122.trial-pivot — Lightweight pub/sub for "the user is about to
 * try and export / download something". This lets the global topbar
 * Trial pill morph into "Upgrade to download" the moment any export
 * panel opens, any download button is hovered, or any 402 export
 * upgrade response comes back from the API — without coupling the
 * pill to every individual call site.
 *
 * No state library: we keep state on `window.__cpcExportIntent` and
 * dispatch a CustomEvent so subscribers re-render. This is plenty for
 * a transient UI signal.
 */

import { useEffect } from "react";

const EVENT = "coparent:export-intent";

/** Fire the intent signal. `ttl_ms` controls how long the morph
 *  stays active (default 8s for hover/focus; the response interceptor
 *  passes ~10s so the upgrade toast and the pill agree). */
export function broadcastExportIntent({ feature, ttl_ms = 8000 } = {}) {
  if (typeof window === "undefined") return;
  if (window.__cpcExportIntentTimer) {
    clearTimeout(window.__cpcExportIntentTimer);
  }
  window.__cpcExportIntent = { active: true, feature: feature || null, at: Date.now() };
  window.dispatchEvent(new CustomEvent(EVENT, { detail: window.__cpcExportIntent }));
  window.__cpcExportIntentTimer = setTimeout(() => {
    window.__cpcExportIntent = { active: false, feature: null, at: Date.now() };
    window.dispatchEvent(new CustomEvent(EVENT, { detail: window.__cpcExportIntent }));
    window.__cpcExportIntentTimer = null;
  }, ttl_ms);
}

/** Clear the intent immediately (e.g. user closed the export panel). */
export function clearExportIntent() {
  if (typeof window === "undefined") return;
  if (window.__cpcExportIntentTimer) {
    clearTimeout(window.__cpcExportIntentTimer);
    window.__cpcExportIntentTimer = null;
  }
  window.__cpcExportIntent = { active: false, feature: null, at: Date.now() };
  window.dispatchEvent(new CustomEvent(EVENT, { detail: window.__cpcExportIntent }));
}

/** Subscribe a component to the intent signal. Use the snapshot
 *  returned from `useState`/`useReducer` upstream; this just wires up
 *  the listener. */
export function useExportIntentEvent(callback) {
  useEffect(() => {
    const fn = (e) => { try { callback(e.detail); } catch { /* swallow */ } };
    window.addEventListener(EVENT, fn);
    return () => window.removeEventListener(EVENT, fn);
  }, [callback]);
}

/** Convenience: a button-level hook that fires the intent on hover
 *  AND focus, and clears it on mouse-leave / blur. Use it on any
 *  download trigger to give the topbar pill a calm pre-tap morph. */
export function makeExportIntentHandlers(feature) {
  return {
    onMouseEnter: () => broadcastExportIntent({ feature, ttl_ms: 8000 }),
    onFocus:      () => broadcastExportIntent({ feature, ttl_ms: 8000 }),
    // We do NOT clear on leave — the timer takes care of fade-out so
    // the pill doesn't flicker as the user moves between adjacent
    // buttons in an export panel.
  };
}
